/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-lines */
/* eslint-disable import/max-dependencies */
import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { FC, useEffect, useState } from 'react';
import LabeledField from 'src/components/Form/LabeledField';
import CustomDateRangePicker from './CustomDatePicker';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import Divider from '@mui/material/Divider';
import { PercentTwo, Plus } from '@im/ui-lib/zenith/icons';
import IconButton from '@mui/material/IconButton';
import DialogWrapper from './DialogWrapper';
import ListItemText from '@mui/material/ListItemText';
import {
  useCreateIngramDiscount,
  useUpdateIngramDiscount,
} from '../hooks/ingramProgramsMutations';
import { IngramDiscount } from '../hooks/ingramProgramsSearch';
import useSnackBar from 'src/hooks/useSnackBar';
import { CenterLoader } from '@im/ui-lib/CenterLoader';
import {
  useIngramProgramDiscountForm,
  useIsIngramProgramDiscountFormUpdated,
} from '../hooks/ingramProgramForms';
import Button from '@mui/material/Button';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import EditIcon from '@mui/icons-material/Edit';

interface DiscountFormProps {
  buttonName: string;
  discountDetails?: IngramDiscount;
  ingramProgramId: number;
  popOverClose?: () => void;
}

const DiscountForm: FC<DiscountFormProps> = ({
  buttonName,
  discountDetails,
  ingramProgramId,
  popOverClose,
}) => {
  const translate = useTranslate();
  const { showSnackBar } = useSnackBar();
  const [openDiscountDialog, setOpenDiscountDialog] = useState<boolean>(false);
  const [formData, setFormData] = useIngramProgramDiscountForm(discountDetails);
  const [isFormUpdated] = useIsIngramProgramDiscountFormUpdated();
  const [isRequiredFilled, setIsRequiredFilled] = useState<boolean>(true);

  useEffect(() => {
    if (!discountDetails) {
      setFormData(undefined);
    }
  }, [discountDetails]);

  useEffect(() => {
    setIsRequiredFilled(
      !formData?.name ||
        !formData?.discount_calc_type ||
        !formData?.discount_type ||
        !formData?.discount_amount ||
        !formData?.effective_date ||
        !formData?.expiration_date
    );
  }, [formData]);

  const getApplicabilityArray = (selectedApplyTo: string[]) => {
    return applyToTypes.map((type) => {
      if (selectedApplyTo.includes(type.value)) {
        return { attributename: type.value, attributevalue: 'True' };
      } else {
        return { attributename: type.value, attributevalue: 'False' };
      }
    });
  };

  const handleFieldChange = (field: string, value: string | string[]) => {
    switch (field) {
      case 'applicability':
        if (typeof value !== 'string') {
          setFormData((prev) => ({
            ...prev,
            applicability: getApplicabilityArray(value),
          }));
        }
        break;
      case 'effective_date':
        if (typeof value === 'string')
          setFormData((prev) => ({ ...prev, effective_date: value }));
        break;
      case 'expiration_date':
        if (typeof value === 'string')
          setFormData((prev) => ({ ...prev, expiration_date: value }));
        break;
      default:
        setFormData((prev) => ({ ...prev, [field]: value }));
        break;
    }
  };
  const discountCalcTypes = [
    { label: 'Reseller price', value: 'reseller_price' },
    { label: 'Percentage discount', value: 'pct_discount' },
  ];
  const discountTypes = [{ label: 'Percentage', value: 'percentage' }];
  const applyToTypes = [
    'Applicability_Sales',
    'Applicability_Renewals',
    'Applicability_Transfer',
    'Applicability_Upgrade',
    'Applicability_Downgrade',
  ].map((o) => ({ label: o.split('_')[1], value: o }));

  const createDiscount = useCreateIngramDiscount({
    ...formData,
    program_id: ingramProgramId,
  });

  const updateDiscount = useUpdateIngramDiscount(formData);
  const handleOnClose = () => {
    setOpenDiscountDialog(false);
    setFormData(undefined);
    if (popOverClose) {
      popOverClose();
    }
  };
  const handleOnSubmit = () => {
    (discountDetails ? updateDiscount : createDiscount)
      .mutateAsync()
      .then(() => {
        handleOnClose();
        showSnackBar({
          autoHide: 2000,
          message: translate(
            discountDetails
              ? 'IngramProgram.EditDiscountSuccess'
              : 'IngramProgram.AddNewDiscountSuccess'
          ),
          severity: 'success',
        });
      })
      .catch(() => {
        showSnackBar({
          autoHide: 2000,
          message: translate(
            discountDetails
              ? 'IngramProgram.EditDiscountError'
              : 'IngramProgram.AddNewDiscountError'
          ),
          severity: 'error',
        });
      });
  };
  return (
    <>
      {!discountDetails ? (
        <Button
          onClick={() => setOpenDiscountDialog(true)}
          startIcon={<Plus />}
          variant="newContained"
        >
          {buttonName}
        </Button>
      ) : (
        <ListItemButton onClick={() => setOpenDiscountDialog(true)}>
          <ListItemIcon>
            <EditIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary={buttonName} />
        </ListItemButton>
      )}
      <DialogWrapper
        dialogTitle={translate(
          discountDetails
            ? 'IngramProgram.EditDiscount'
            : 'IngramProgram.AddNewDiscount'
        )}
        disabled={isRequiredFilled}
        isFormUpdated={isFormUpdated}
        onClose={handleOnClose}
        onSubmit={handleOnSubmit}
        open={openDiscountDialog}
      >
        <Box>
          {createDiscount.isLoading ||
            (updateDiscount.isLoading && <CenterLoader overlay={true} />)}
          <Stack gap={1}>
            <LabeledField isRequired label={translate('Common.Title')}>
              <TextField
                aria-describedby={translate('Common.Title')}
                disabled={Boolean(discountDetails)}
                helperText={`${
                  50 - (formData.name?.length ?? 0)
                } characters left`}
                inputProps={{ maxLength: 50 }}
                margin="normal"
                name="name"
                onChange={(event) =>
                  handleFieldChange(event.target.name, event.target.value)
                }
                placeholder={translate('Common.TitlePlaceholder')}
                sx={{
                  margin: '0px',
                }}
                value={formData.name}
              />
            </LabeledField>
            <Stack
              direction={'row'}
              justifyContent={'space-between'}
              width={'100%'}
            >
              <LabeledField
                isRequired
                label={translate('Common.Date')}
                sx={{ width: '49%' }}
              >
                <CustomDateRangePicker
                  effectiveDate={formData.effective_date}
                  expirationDate={formData.expiration_date}
                  handleFieldChange={handleFieldChange}
                />
              </LabeledField>
              <LabeledField
                isRequired
                label={translate('IngramProgram.DiscountCalculation')}
                sx={{ width: '49%' }}
              >
                <Select
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 4.5 + 8,
                      },
                    },
                  }}
                  displayEmpty
                  fullWidth
                  input={<OutlinedInput />}
                  name="discount_calc_type"
                  onChange={(event) =>
                    handleFieldChange(event.target.name, event.target.value)
                  }
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <em style={{ color: '#8B8B8B' }}>
                          {translate('IngramProgram.SelectPricePlaceholder')}
                        </em>
                      );
                    }

                    return (
                      discountCalcTypes.filter(
                        (disCalc) => disCalc.value === selected
                      )[0].label ?? ''
                    );
                  }}
                  sx={{ fontSize: '14px', height: '34px' }}
                  value={formData.discount_calc_type ?? ''}
                >
                  {discountCalcTypes.map((option: any) => (
                    <MenuItem key={`${option.value}`} value={option.value}>
                      <Typography variant="bodycopyregular">
                        {option.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </LabeledField>
            </Stack>
            <Stack
              direction={'row'}
              justifyContent={'space-between'}
              width={'100%'}
            >
              <LabeledField
                isRequired
                label={translate('IngramProgram.DiscountType')}
                sx={{ width: '49%' }}
              >
                <Select
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 4.5 + 8,
                      },
                    },
                  }}
                  displayEmpty
                  fullWidth
                  input={<OutlinedInput />}
                  name="discount_type"
                  onChange={(event) =>
                    handleFieldChange(event.target.name, event.target.value)
                  }
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <em style={{ color: '#8B8B8B' }}>
                          {translate(
                            'IngramProgram.DiscountCalculationPlaceholder'
                          )}
                        </em>
                      );
                    }

                    return (
                      discountTypes.filter(
                        (disType) => disType.value === selected
                      )[0].label ?? ''
                    );
                  }}
                  sx={{ fontSize: '14px', height: '34px' }}
                  value={formData.discount_type ?? ''}
                >
                  {discountTypes.map((option: any) => (
                    <MenuItem key={`${option.value}`} value={option.value}>
                      <Typography variant="bodycopyregular">
                        {option.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </LabeledField>
              <LabeledField
                isRequired
                label={translate('IngramProgram.DiscountAmount')}
                sx={{ width: '49%' }}
              >
                <TextField
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Divider orientation="vertical" />
                        <IconButton>
                          <PercentTwo />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  aria-describedby={translate('IngramProgram.DiscountAmount')}
                  margin="normal"
                  name="discount_amount"
                  onChange={(event) => {
                    const { value } = event.target;
                    if (
                      !isNaN(Number(value)) &&
                      Number(value) >= 0 &&
                      Number(value) <= 100
                    ) {
                      handleFieldChange(event.target.name, event.target.value);
                    }
                  }}
                  placeholder={translate(
                    'IngramProgram.DiscountAmountPlaceholder'
                  )}
                  sx={{
                    margin: '0px',
                  }}
                  value={formData.discount_amount}
                />
              </LabeledField>
            </Stack>
            <LabeledField label={translate('IngramProgram.ApplyTo')}>
              <Select
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 4.5 + 8,
                    },
                  },
                }}
                displayEmpty
                fullWidth
                input={<OutlinedInput />}
                multiple
                name="applicability"
                onChange={({ target: { value } }) =>
                  handleFieldChange(
                    'applicability',
                    typeof value === 'string' ? value.split(',') : value
                  )
                }
                renderValue={(selected) => {
                  if (!selected.length) {
                    return (
                      <em style={{ color: '#8B8B8B' }}>
                        {translate('IngramProgram.ApplyToPlaceholder')}
                      </em>
                    );
                  }

                  return selected.map((val) => val.split('_')[1]).join(', ');
                }}
                sx={{ fontSize: '14px', height: '34px' }}
                value={
                  formData.applicability
                    ?.filter((value) => value.attributevalue === 'True')
                    .map((app) => app.attributename) ?? []
                }
              >
                {applyToTypes.map((option: any) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox
                      checked={formData.applicability
                        ?.filter((value) => value.attributevalue === 'True')
                        .map((app) => app.attributename)
                        .includes(option.value)}
                    />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </LabeledField>
            <FormControl>
              <FormControlLabel
                control={<Checkbox checked={formData.status_code === 'A'} />}
                label={translate('IngramProgram.Activated')}
                name="status_code"
                onChange={(event, checked) =>
                  handleFieldChange('status_code', checked ? 'A' : 'D')
                }
                sx={{ marginLeft: '2px' }}
              />
              <FormHelperText>
                {translate('IngramProgram.ActivatedPlaceholder')}
              </FormHelperText>
            </FormControl>
          </Stack>
        </Box>
      </DialogWrapper>
    </>
  );
};

export default DiscountForm;
